/**
 * React Query hooks for PF-3a (fieldiq-api#1219): anomalies, digs, features,
 * repairs, welds, NDT / PT links and dig reports.
 *
 * Same rules as usePipelineFacility.ts, whose helpers these use: every key
 * starts ['pipelineFacility'] and every write invalidates that one prefix (a
 * feature moves the dig's counts, the register, the job's Digs tab and the
 * anomaly's "on dig" column); every read returns the meta and spreads
 * LOAD_FAILURE_QUERY_OPTIONS so a refusal reads as a refusal, never as empty.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidatePf, unwrapPf, unwrapPfField, unwrapPfList } from './usePipelineFacility';
import type {
  PfAnomaly,
  PfAnomalyImportBody,
  PfAnomalyImportResult,
  PfAnomalyParseResult,
  PfAnomalyWrite,
  PfDig,
  PfDigCreate,
  PfDigDetail,
  PfDigFeature,
  PfDigPatch,
  PfDigRepair,
  PfFeatureWrite,
  PfFieldDig,
  PfFieldDigDetail,
  PfFieldDigFeature,
  PfNdtCandidate,
  PfNdtLink,
  PfPtCandidate,
  PfPtLink,
  PfRepairWrite,
  PfReport,
  PfReportDetail,
  PfReportWrite,
  PfReportAiSummaryResult,
  PfWeld,
  PfWeldWrite,
} from '../types/pf-digs';

type Params = Record<string, string | undefined>;
const clean = (p?: Params) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  Object.entries(p).forEach(([k, v]) => {
    if (v) out[k] = v;
  });
  return Object.keys(out).length ? out : undefined;
};

export const pfDigKeys = {
  anomalies: (p?: Params) => ['pipelineFacility', 'anomalies', clean(p) ?? {}] as const,
  anomaly: (id: string) => ['pipelineFacility', 'anomaly', id] as const,
  digs: (p?: Params) => ['pipelineFacility', 'digs', clean(p) ?? {}] as const,
  dig: (id: string) => ['pipelineFacility', 'dig', id] as const,
  welds: (p?: Params) => ['pipelineFacility', 'welds', clean(p) ?? {}] as const,
  weld: (id: string) => ['pipelineFacility', 'weld', id] as const,
  weldUploads: (id: string) => ['pipelineFacility', 'weldUploads', id] as const,
  reports: (p?: Params) => ['pipelineFacility', 'reports', clean(p) ?? {}] as const,
  report: (id: string) => ['pipelineFacility', 'report', id] as const,
  ndtCandidates: (q: string) => ['pipelineFacility', 'ndtCandidates', q] as const,
  ptCandidates: (q: string) => ['pipelineFacility', 'ptCandidates', q] as const,
  fieldDigs: () => ['pipelineFacility', 'field', 'digs'] as const,
  fieldDig: (id: string) => ['pipelineFacility', 'field', 'dig', id] as const,
};

function usePfWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidatePf(qc) });
}

// ─── Anomalies ───────────────────────────────────────────────────────────────

export interface PfAnomalyFilters extends Params {
  siteId?: string;
  segmentId?: string;
  featureType?: string;
  q?: string;
  undug?: string;
}

export function usePfAnomalies(filters?: PfAnomalyFilters, enabled = true) {
  return useQuery({
    queryKey: pfDigKeys.anomalies(filters),
    queryFn: async () => unwrapPfList<PfAnomaly>((await api.pipelineFacility.anomalies.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfAnomaly(id: string | undefined) {
  return useQuery({
    queryKey: pfDigKeys.anomaly(id ?? ''),
    queryFn: async () => unwrapPf<PfAnomaly>((await api.pipelineFacility.anomalies.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreatePfAnomaly = () =>
  usePfWrite(async (body: PfAnomalyWrite) => unwrapPf<PfAnomaly>((await api.pipelineFacility.anomalies.create(body)).data));
export const useUpdatePfAnomaly = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfAnomalyWrite }) =>
    unwrapPf<PfAnomaly>((await api.pipelineFacility.anomalies.update(id, body)).data));
export const useDeletePfAnomaly = () => usePfWrite(async (id: string) => (await api.pipelineFacility.anomalies.remove(id)).data);

/** The confirm half of an import. The result keeps `skipped[]` whole — the screen must show it. */
export const useImportPfAnomalies = () =>
  usePfWrite(async (body: PfAnomalyImportBody) => {
    const { data } = unwrapPf<PfAnomalyImportResult>((await api.pipelineFacility.anomalies.importConfirm(body)).data);
    const inserted = Array.isArray(data?.inserted) ? data!.inserted : [];
    const skipped = Array.isArray(data?.skipped) ? data!.skipped : [];
    return {
      inserted,
      skipped,
      counts: {
        inserted: typeof data?.counts?.inserted === 'number' ? data.counts.inserted : inserted.length,
        skipped: typeof data?.counts?.skipped === 'number' ? data.counts.skipped : skipped.length,
      },
    } as PfAnomalyImportResult;
  });

/**
 * PF-3b: the AI reads the operator's list and PROPOSES rows. It saves no
 * anomaly, so it invalidates nothing. A non-2xx answer rejects (axios), so a
 * 503 / 502 can never be read as a parse.
 */
export const useParsePfAnomalyList = () =>
  useMutation({
    mutationFn: async ({ file, siteId, iliVendor, iliRunDate }: { file: File; siteId: string; iliVendor?: string | null; iliRunDate?: string | null }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('site_id', siteId);
      if (iliVendor) form.append('ili_vendor', iliVendor);
      if (iliRunDate) form.append('ili_run_date', iliRunDate);
      // No Content-Type: the browser sets the multipart boundary (client.ts interceptor).
      const { data } = unwrapPf<PfAnomalyParseResult>((await api.pipelineFacility.anomalies.importParse(form)).data);
      if (!data || typeof data.runId !== 'string' || !Array.isArray(data.rows)) throw new Error('The AI read returned no rows to review.');
      return data;
    },
  });

/** PF-3b: abandon a parse. Saves nothing either. */
export const useDismissPfAnomalyParse = () =>
  useMutation({ mutationFn: async (runId: string) => (await api.pipelineFacility.anomalies.dismissParse(runId)).data });

// ─── Digs ────────────────────────────────────────────────────────────────────

export interface PfDigFilters extends Params {
  jobId?: string;
  siteId?: string;
  status?: string;
  q?: string;
}

export function usePfDigs(filters?: PfDigFilters, enabled = true) {
  return useQuery({
    queryKey: pfDigKeys.digs(filters),
    queryFn: async () => unwrapPfList<PfDig>((await api.pipelineFacility.digs.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfDig(id: string | undefined) {
  return useQuery({
    queryKey: pfDigKeys.dig(id ?? ''),
    queryFn: async () => unwrapPf<PfDigDetail>((await api.pipelineFacility.digs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreatePfDig = () =>
  usePfWrite(async ({ jobId, body }: { jobId: string; body: PfDigCreate }) =>
    unwrapPf<PfDigDetail>((await api.pipelineFacility.digs.create(jobId, body)).data));
export const useUpdatePfDig = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfDigPatch }) =>
    unwrapPf<PfDigDetail>((await api.pipelineFacility.digs.update(id, body)).data));
export const useDeletePfDig = () => usePfWrite(async (id: string) => (await api.pipelineFacility.digs.remove(id)).data);

export const useAddPfFeature = () =>
  usePfWrite(async ({ digId, body }: { digId: string; body: PfFeatureWrite }) =>
    unwrapPf<PfDigFeature>((await api.pipelineFacility.digs.addFeature(digId, body)).data));
export const useUpdatePfFeature = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfFeatureWrite }) =>
    unwrapPf<PfDigFeature>((await api.pipelineFacility.digFeatures.update(id, body)).data));
export const useDeletePfFeature = () => usePfWrite(async (id: string) => (await api.pipelineFacility.digFeatures.remove(id)).data);

export const useAddPfRepair = () =>
  usePfWrite(async ({ digId, body }: { digId: string; body: PfRepairWrite }) =>
    unwrapPf<PfDigRepair>((await api.pipelineFacility.digs.addRepair(digId, body)).data));
export const useUpdatePfRepair = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfRepairWrite }) =>
    unwrapPf<PfDigRepair>((await api.pipelineFacility.digRepairs.update(id, body)).data));
export const useDeletePfRepair = () => usePfWrite(async (id: string) => (await api.pipelineFacility.digRepairs.remove(id)).data);

/**
 * Every dig repair on a job, labelled — for the weld form's "Dig repair" choice.
 * The dig list carries no repairs, so each dig's detail is read (a job has a
 * handful of digs).
 */
export function usePfJobRepairs(jobId: string | undefined) {
  const digs = usePfDigs({ jobId }, !!jobId);
  const ids = (digs.data?.data ?? []).map((d) => d.id);
  const details = useQueries({
    queries: ids.map((id) => ({
      queryKey: pfDigKeys.dig(id),
      queryFn: async () => unwrapPf<PfDigDetail>((await api.pipelineFacility.digs.get(id)).data),
      ...LOAD_FAILURE_QUERY_OPTIONS,
    })),
  });
  const repairs = details.flatMap((q) => {
    const d = q.data?.data;
    if (!d) return [];
    return d.repairs.map((r) => {
      const f = d.features.find((x) => x.id === r.featureId);
      return { id: r.id, label: `${d.digNumber} · ${r.methodLabel ?? r.method}${f ? ` · ${f.anomalyFeatureId ?? 'found in ditch'}` : ''}` };
    });
  });
  return { repairs, isLoading: digs.isLoading || details.some((q) => q.isLoading) };
}

// ─── Welds and links ─────────────────────────────────────────────────────────

export interface PfWeldFilters extends Params {
  jobId?: string;
  digRepairId?: string;
  q?: string;
}

export function usePfWelds(filters?: PfWeldFilters, enabled = true) {
  return useQuery({
    queryKey: pfDigKeys.welds(filters),
    queryFn: async () => unwrapPfList<PfWeld>((await api.pipelineFacility.welds.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfWeld(id: string | undefined) {
  return useQuery({
    queryKey: pfDigKeys.weld(id ?? ''),
    queryFn: async () => unwrapPf<PfWeld>((await api.pipelineFacility.welds.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreatePfWeld = () =>
  usePfWrite(async ({ jobId, body }: { jobId: string; body: PfWeldWrite }) =>
    unwrapPf<PfWeld>((await api.pipelineFacility.welds.create(jobId, body)).data));
export const useUpdatePfWeld = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfWeldWrite }) =>
    unwrapPf<PfWeld>((await api.pipelineFacility.welds.update(id, body)).data));
export const useDeletePfWeld = () => usePfWrite(async (id: string) => (await api.pipelineFacility.welds.remove(id)).data);

export const useLinkPfNdt = () =>
  usePfWrite(async ({ weldId, ndtWeldUid }: { weldId: string; ndtWeldUid: string }) =>
    unwrapPf<PfNdtLink>((await api.pipelineFacility.welds.linkNdt(weldId, { ndt_weld_uid: ndtWeldUid })).data));
export const useUnlinkPfNdt = () => usePfWrite(async (id: string) => (await api.pipelineFacility.welds.unlinkNdt(id)).data);
export const useLinkPfPt = () =>
  usePfWrite(async (body: { pt_test_id: string; dig_repair_id?: string | null; segment_id?: string | null }) =>
    unwrapPf<PfPtLink>((await api.pipelineFacility.ptLinks.create(body)).data));
export const useUnlinkPfPt = () => usePfWrite(async (id: string) => (await api.pipelineFacility.ptLinks.remove(id)).data);

export function usePfNdtCandidates(q: string, enabled: boolean) {
  return useQuery({
    queryKey: pfDigKeys.ndtCandidates(q),
    queryFn: async () => unwrapPfList<PfNdtCandidate>((await api.pipelineFacility.linkCandidates.ndtWelds(q || undefined)).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfPtCandidates(q: string, enabled: boolean) {
  return useQuery({
    queryKey: pfDigKeys.ptCandidates(q),
    queryFn: async () => unwrapPfList<PfPtCandidate>((await api.pipelineFacility.linkCandidates.ptTests(q || undefined)).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The weld's pf_weld uploads (uploads.id is what PATCH ndt_report_upload_id and the download take). */
export function usePfWeldUploads(weldId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: pfDigKeys.weldUploads(weldId ?? ''),
    queryFn: async () => {
      const body = (await api.uploads.listForEntity('pf_weld', weldId as string)).data as { data?: unknown };
      return Array.isArray(body?.data) ? (body.data as { id: string; s3Key?: string; key?: string; fileName?: string; originalName?: string }[]) : [];
    },
    enabled: !!weldId && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── Reports ─────────────────────────────────────────────────────────────────

export interface PfReportFilters extends Params {
  jobId?: string;
  digId?: string;
  status?: string;
}

export function usePfReports(filters?: PfReportFilters, enabled = true) {
  return useQuery({
    queryKey: pfDigKeys.reports(filters),
    queryFn: async () => unwrapPfList<PfReport>((await api.pipelineFacility.reports.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfReport(id: string | undefined) {
  return useQuery({
    queryKey: pfDigKeys.report(id ?? ''),
    queryFn: async () => unwrapPf<PfReportDetail>((await api.pipelineFacility.reports.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreatePfReport = () =>
  usePfWrite(async ({ digId, body }: { digId: string; body: PfReportWrite }) =>
    unwrapPf<PfReportDetail>((await api.pipelineFacility.digs.createReport(digId, body)).data));
export const useUpdatePfReport = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfReportWrite }) =>
    unwrapPf<PfReportDetail>((await api.pipelineFacility.reports.update(id, body)).data));
export const useDeletePfReport = () => usePfWrite(async (id: string) => (await api.pipelineFacility.reports.remove(id)).data);
/** PF-3b: ask the AI for a summary draft (held in a pending run; the report row is unchanged until approved). */
export const useDraftPfReportSummary = () =>
  usePfWrite(async (id: string) => unwrapPf<PfReportAiSummaryResult>((await api.pipelineFacility.reports.aiSummary(id)).data));
/** PF-3b: approve the draft — `summary` only when the person changed it. */
export const useApprovePfReportSummary = () =>
  usePfWrite(async ({ id, runId, summary }: { id: string; runId: string; summary?: string }) =>
    unwrapPf<PfReportDetail>(
      (await api.pipelineFacility.reports.approveAiSummary(id, summary === undefined ? { run_id: runId } : { run_id: runId, summary })).data,
    ));
export const useDismissPfReportSummary = () =>
  usePfWrite(async ({ id, runId }: { id: string; runId: string }) =>
    (await api.pipelineFacility.reports.dismissAiSummary(id, { run_id: runId })).data);
export const useIssuePfReport = () =>
  usePfWrite(async (id: string) => unwrapPf<PfReportDetail>((await api.pipelineFacility.reports.issue(id)).data));

// ─── Field (snake_case) ──────────────────────────────────────────────────────

export function usePfFieldDigs() {
  return useQuery({
    queryKey: pfDigKeys.fieldDigs(),
    queryFn: async () => {
      const r = unwrapPfField<PfFieldDig[]>((await api.pipelineFacility.field.digs()).data);
      return { data: Array.isArray(r.data) ? r.data : [], meta: r.meta };
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfFieldDig(id: string | undefined) {
  return useQuery({
    queryKey: pfDigKeys.fieldDig(id ?? ''),
    queryFn: async () => unwrapPfField<PfFieldDigDetail>((await api.pipelineFacility.field.dig(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useUpdatePfFieldDig = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfDigPatch }) =>
    unwrapPfField<PfFieldDigDetail>((await api.pipelineFacility.field.updateDig(id, body)).data));
export const useAddPfFieldFeature = () =>
  usePfWrite(async ({ digId, body }: { digId: string; body: PfFeatureWrite }) =>
    unwrapPfField<PfFieldDigFeature>((await api.pipelineFacility.field.addFeature(digId, body)).data));
export const useUpdatePfFieldFeature = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfFeatureWrite }) =>
    unwrapPfField<PfFieldDigFeature>((await api.pipelineFacility.field.updateFeature(id, body)).data));
